import cronstrue from 'cronstrue';

export interface QueryParameterValue {
    name: string;
    type: 'STRING' | 'NUMBER' | 'BOOLEAN' | 'DATE' | 'TIMESTAMP' | 'STRING_LIST';
    value: string;
}

export const extractParametersFromSql = (sql: string): QueryParameterValue[] => {
    // Regex that matches:
    // 1. String literals (single quotes, handling escaped quotes via '')
    // 2. Single line comments (-- ...)
    // 3. Block comments (/* ... */)
    // 4. Bind variables (:variable) - this is what we capture
    // The order matters!
    const regex = /'(?:''|[^'])*'|--[^\n]*|\/\*[\s\S]*?\*\/|(?<!:):([a-zA-Z0-9_]+)/g;

    const matches = Array.from(sql.matchAll(regex));
    const uniqueParams = new Set<string>();
    const parameters: QueryParameterValue[] = [];

    for (const match of matches) {
        // match[1] corresponds to the capturing group for bind variables.
        // It will be undefined if the regex matched a string or comment.
        const paramName = match[1];

        // We only care if we found a parameter name (not inside string/comment)
        if (paramName && !uniqueParams.has(paramName)) {
            uniqueParams.add(paramName);
            parameters.push({
                name: paramName,
                type: 'STRING',
                value: '',
            });
        }
    }

    return parameters;
};

export const formatCronExpression = (cron: string | undefined): string => {
    if (!cron) return 'Not set';
    try {
        // Strict 6-field check since our backend/CRON preset requires 6 fields
        const parts = cron.trim().split(/\s+/);
        if (parts.length !== 6) {
            return 'Invalid cron expression: Must have exactly 6 fields';
        }

        // Basic sanity check: Cronstrue sometimes parses hyphens without numbers as valid
        const basicValidRegex = /^[0-9A-Z\*\/\-\,\?]+$/i;
        for (const part of parts) {
            if (!basicValidRegex.test(part) || part === '-' || part.endsWith('-') || part.startsWith('-')) {
                return 'Invalid cron expression: Contains invalid characters';
            }
        }

        // We use { throwExceptionOnParseError: true } so we can fallback to the raw cron
        // if it's incomplete while the user is typing it out
        return cronstrue.toString(cron, { throwExceptionOnParseError: true });
    } catch (e) {
        return 'Invalid cron expression';
    }
};

export const isValidCronExpression = (cron: string | undefined): boolean => {
    if (!cron) return false;
    try {
        // Strict 6-field check
        const parts = cron.trim().split(/\s+/);
        if (parts.length !== 6) return false;

        const basicValidRegex = /^[0-9A-Z\*\/\-\,\?]+$/i;
        for (const part of parts) {
            if (!basicValidRegex.test(part) || part === '-' || part.endsWith('-') || part.startsWith('-')) {
                return false;
            }
        }

        cronstrue.toString(cron, { throwExceptionOnParseError: true });
        return true;
    } catch (e) {
        return false;
    }
};

export const getAppRoot = (appIdOrRole: string): string => {
    if (!appIdOrRole) return '';
    const lastDot = appIdOrRole.lastIndexOf('.');
    if (lastDot > 0) {
        return appIdOrRole.substring(0, lastDot).toLowerCase();
    }
    return appIdOrRole.toLowerCase();
};

